import { useCallback, useState } from "react";
import { AlertTriangle } from "lucide-react";
import type { ReactNode } from "react";
import { Badge } from "@/components/atoms/studio/ui/badge";
import { RunStatusBadge } from "@/components/organisms/studio/RunStatusBadge";
import { EngineApprovalGate } from "./EngineApprovalGate";
import { EngineTaskCard } from "./EngineTaskCard";
import { EMPTY_REVIEW_STATE, type TaskReviewState } from "./reviewState";
import { planSuccessSuppressed, toneBadgeVariant, undeclaredSummary } from "@/lib/migrations/coverage";
import { familyLabel, familyPresentation } from "@/lib/migrations/families";
import { validationCountsSummary } from "@/lib/studio/validation";
import type { EnginePlanReport } from "@/services/migrations/engine";

/**
 * The whole verdict for one engine plan.
 *
 * Silent loss is decided PLAN-WIDE and pushed down: one task that dropped an effect
 * without a reason suppresses every success affordance on the screen, including on the
 * tasks that were clean. A reader who sees three green tasks and one red one will read
 * the green ones; a plan that lost something silently has to be read as a whole.
 *
 * THE PLAN TOTAL IS THE SERVER'S. `counts.undeclared_total` is summed over every task on
 * the plan, not over the tasks in this payload — and it is rendered at zero, at any
 * positive number, and (distinctly) when it never arrived, because "we did not receive a
 * count" and "the count was zero" are the two states this screen must never merge.
 *
 * IT ALSO HOLDS THE REVIEW STATE. Each task card reports which artifact it is showing;
 * the approval gate below reads all of it and refuses to offer a decision on a task whose
 * current artifact is not on screen. That has to be lifted here because approval is one
 * plan-wide act covering every task at the gate.
 */
export function EnginePlanReportView({
  report,
  orgId,
}: {
  report: EnginePlanReport;
  orgId?: string | null;
}) {
  const successSuppressed = planSuccessSuppressed(report);
  const summary = validationCountsSummary(report.counts);
  const undeclared = undeclaredSummary(report.undeclared_total);

  const [review, setReview] = useState<Record<string, TaskReviewState>>({});
  const onReviewChange = useCallback((taskKey: string, patch: Partial<TaskReviewState>) => {
    setReview((current) => {
      const previous = current[taskKey] ?? EMPTY_REVIEW_STATE;
      const next = { ...previous, ...patch };
      if (
        previous.loaded === next.loaded &&
        previous.artifacts === next.artifacts &&
        previous.displayed === next.displayed
      ) {
        return current;
      }
      return { ...current, [taskKey]: next };
    });
  }, []);

  return (
    <div className="space-y-5">
      <div className="overflow-hidden rounded-xl border border-border bg-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border bg-muted/20 px-5 py-3">
          <div className="flex min-w-0 flex-wrap items-center gap-2">
            <RunStatusBadge status={report.status} />
            <span className="truncate font-mono text-sm text-foreground">
              {report.title || report.family || report.plan_id}
            </span>
          </div>
          <div className="flex shrink-0 flex-wrap items-center gap-2">
            {report.counts.source_partial > 0 ? (
              <Badge
                variant="warm"
                title="Tasks whose source could not be read in full. Their coverage fractions are measured against a partial read."
              >
                {report.counts.source_partial} partial source
              </Badge>
            ) : null}
            {report.counts.refused > 0 ? (
              <Badge variant="destructive">{report.counts.refused} refused</Badge>
            ) : null}
            {/* Present tense, and never inferred: a component of this plan exists in the
                customer's org right now. */}
            {report.wrote_to_org ? (
              <Badge variant="warm" title="Components of this plan exist in the org because of it.">
                {report.applied} written to the org
              </Badge>
            ) : (
              <Badge variant="secondary">nothing written to the org</Badge>
            )}
            {/* ALWAYS shown, including at zero — the engine reports it at zero on
                purpose, and a bucket that only appears when non-empty teaches readers
                to stop looking for it. */}
            <Badge
              variant={toneBadgeVariant(undeclared.tone)}
              title={
                !undeclared.known
                  ? "The report did not carry an undeclared total. Absent is not zero."
                  : undeclared.tone === "destructive"
                    ? "Effects dropped with no declared reason. Nothing says what was lost."
                    : "Nothing was dropped without a reason."
              }
            >
              {undeclared.label}
            </Badge>
          </div>
        </div>

        <div className="space-y-4 p-5">
          {successSuppressed ? (
            <PlanSilentLossBanner
              count={report.undeclared_total}
              known={undeclared.known}
            />
          ) : null}

          <dl className="flex flex-wrap gap-x-6 gap-y-2 font-mono text-[11px] text-muted-foreground">
            {/* The family's own name, not its wire identifier. An unknown family falls
                back to the raw string rather than to a guessed label — a lane nobody here
                has read must look unfamiliar. */}
            <Meta label="family" value={familyLabel(report.family)} />
            <Meta label="tasks" value={String(report.counts.tasks || report.tasks.length)} />
            <Meta label="effects" value={String(report.counts.total_effects)} />
            <Meta label="engine" value={report.engine_version} />
            <Meta label="transform" value={report.transform_version} />
            <Meta label="menus" value={report.menus_sha?.slice(0, 12)} />
          </dl>

          <FamilyNote family={report.family} />

          {summary ? (
            <p className="font-mono text-[11px] uppercase text-muted-foreground">{summary}</p>
          ) : null}

          {/* Said out loud by the server, and repeated here rather than left for a
              reader to notice a list is missing. */}
          {report.carried_omitted ? (
            <p className="max-w-prose font-mono text-[11px] text-muted-foreground">
              The carried list is not in this report — it is the largest and the least surprising
              part of it. Open the Carried bucket on any task to page through it.
            </p>
          ) : null}

          <EngineApprovalGate
            report={report}
            successSuppressed={successSuppressed}
            review={review}
          />
        </div>
      </div>

      {report.tasks.length === 0 ? (
        <p className="font-mono text-xs text-muted-foreground">This plan has no tasks yet.</p>
      ) : (
        report.tasks.map((task) => (
          <EngineTaskCard
            key={task.key}
            planId={report.plan_id}
            task={task}
            family={report.family}
            orgId={orgId ?? report.org_id}
            successSuppressed={successSuppressed}
            onReviewChange={onReviewChange}
          />
        ))
      )}
    </div>
  );
}

function PlanSilentLossBanner({ count, known }: { count: number; known: boolean }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-destructive/40 bg-destructive/10 p-3 text-xs text-destructive">
      <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
      <span className="min-w-0 flex-1 leading-relaxed">
        {known ? (
          <span className="font-semibold">
            This plan dropped {count} effect{count === 1 ? "" : "s"} with no declared reason.
          </span>
        ) : (
          <span className="font-semibold">
            This report did not carry an undeclared total, so it cannot be read as clean.
          </span>
        )}{" "}
        Every success indicator on this page is withheld until that is resolved — including on
        the tasks that were clean, because a plan that lost something silently has to be read as
        a whole.
      </span>
    </div>
  );
}

/**
 * What this family does, and — where it applies — what it LEAVES RUNNING.
 *
 * The second half is the reason this exists. A Process Builder migration finishes with the
 * source process still firing: a process is deactivated through its FlowDefinition's active
 * version, not by redeploying the component, and this engine does not operate that
 * mechanism. Nothing else on a plan report says so, and "applied" on a process plan looks
 * exactly like "applied" on a rule plan that CAN be finished.
 */
function FamilyNote({ family }: { family: string }): ReactNode {
  const presentation = familyPresentation(family);
  if (!presentation) return null;
  return (
    <div className="space-y-1.5">
      <p className="max-w-prose text-xs text-muted-foreground">{presentation.detail}</p>
      {presentation.sourceKeepsRunning ? (
        <p className="flex items-start gap-2 max-w-prose text-xs text-[hsl(var(--accent-warm))]">
          <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
          <span className="min-w-0 flex-1 leading-relaxed">
            {presentation.sourceKeepsRunning}
          </span>
        </p>
      ) : null}
    </div>
  );
}

function Meta({ label, value }: { label: string; value?: string | null }): ReactNode {
  if (!value) return null;
  return (
    <div className="flex gap-1.5">
      <dt className="uppercase tracking-wide text-muted-foreground/60">{label}</dt>
      <dd className="text-foreground">{value}</dd>
    </div>
  );
}
